import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { MantineProvider, ColorSchemeScript } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
import { theme } from './theme';
import { safeLocalStorageColorSchemeManager } from './utils/safeColorSchemeManager';
import { getSkipThemePersist } from './utils/themePersist';

// Import pages
import Splash from './pages/Splash';
import Restaurants from './pages/Restaurants';
import Upload from './pages/Upload';
import FastUpload from './pages/FastUpload';
import UploadCSV from './pages/UploadCSV';
import MenuV2 from './pages/MenuV2';
import MenuAlias from './pages/MenuAlias';
import MenuChat from './pages/MenuChat';
import ModelSelectLanding from './pages/ModelSelectLanding';
import MenuScanIngredient from './pages/MenuScanIngredient';
import MenuScanBeer from './pages/MenuScanBeer';
import Login from './pages/Login';
import Register from './pages/Register';
import QueryLogs from './pages/QueryLogs';
import TestUpload from './pages/TestUpload';
import AdminApp from './admin/AdminApp';

// Import context
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ActiveUploadSessionProvider } from './context/ActiveUploadSessionContext';
import { ColorSchemeManager } from './components/ColorSchemeManager';

// Redirect component for old menu edit route to new admin interface
function MenuEditRedirect() {
  const { menuId } = useParams<{ menuId: string }>();
  return <Navigate to={`/admin/menus/edit/v2/${menuId}`} replace />;
}

// The ingredient scan used to live at /scan-ingredient; keep old links (and ?sample=) working
function ScanIngredientRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/scan${search}`} replace />;
}

function App() {
  return (
    <>
      <ColorSchemeScript defaultColorScheme="light" />
      <MantineProvider
        theme={theme}
        defaultColorScheme="light"
        colorSchemeManager={safeLocalStorageColorSchemeManager({
          getSkipPersist: getSkipThemePersist,
        })}
      >
        <Notifications />
      <BrowserRouter>
        <ActiveUploadSessionProvider>
        <ColorSchemeManager />
        <AuthProvider>
          <CartProvider>
            <Routes>
              {/* Splash page at root */}
              <Route path="/" element={<Splash />} />
              {/* Admin panel (Refine) */}
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="/restaurants" element={<Restaurants />} />
              <Route path="/upload" element={<Upload />} />
              <Route path="/upload-fast" element={<FastUpload />} />
              <Route path="/upload-csv" element={<UploadCSV />} />
              {/* /m/:id is the stable QR code alias — resolves the QR code's current
                  target menu via the API, then redirects to the current menu view */}
              <Route path="/m/:id" element={<MenuAlias />} />
              <Route path="/menus/:menuId" element={<MenuV2 />} />
              <Route path="/menus/v2/:menuId" element={<MenuV2 />} />
              {/* Experimental AI-assistant picker landing page (opt-in per QR code) */}
              <Route path="/menus/:menuId/start" element={<ModelSelectLanding />} />
              <Route path="/menus/:menuId/chat" element={<MenuChat />} />
              {/* Experimental: photograph any menu, tap a line to see what it is */}
              <Route path="/scan" element={<MenuScanIngredient />} />
              <Route path="/scan-v2" element={<MenuScanIngredient autoTranslate yakiniku />} />
              {/* As /scan-v2, but the photo shows no English until a line is tapped */}
              <Route path="/scan-v3" element={<MenuScanIngredient autoTranslate yakiniku revealOnTap />} />
              <Route path="/scan-beer" element={<MenuScanBeer />} />
              <Route path="/scan-ingredient" element={<ScanIngredientRedirect />} />
              {/* Redirect old menu edit route to new admin interface */}
              <Route path="/menus/:menuId/edit" element={<MenuEditRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/query-logs" element={<QueryLogs />} />
              <Route path="/test-upload" element={<TestUpload />} />
            </Routes>
          </CartProvider>
        </AuthProvider>
        </ActiveUploadSessionProvider>
      </BrowserRouter>
      </MantineProvider>
    </>
  );
}

export default App;
